import React, { useState, useEffect, useCallback } from 'react';
import { getSupabase } from './supabaseClient';
import { LLM_USER_TOKEN_LIMIT, getBackendURL } from './env.js';
import {
  Box,
  Heading,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  Input,
  HStack,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Progress,
  useToast,
  Flex,
  Icon,
  Tooltip,
} from '@chakra-ui/react';

const API_BASE_URL = getBackendURL();

const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return num.toLocaleString();
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString();
};

const formatPercent = (val) => {
  if (val === null || val === undefined) return '0%';
  return `${val.toFixed(1)}%`;
};

const getModelBadgeColor = (modelName) => {
  if (!modelName) return 'gray';
  const name = modelName.toLowerCase();
  if (name.includes('gemini') || name.includes('google') || name.includes('flash') || name.includes('pro')) return 'teal';
  if (name.includes('gpt') || name.includes('openai') || name.includes('o1') || name.includes('o3')) return 'purple';
  if (name.includes('claude') || name.includes('anthropic') || name.includes('sonnet')) return 'orange';
  if (name.includes('cohere')) return 'pink';
  if (name.includes('llama') || name.includes('meta')) return 'blue';
  return 'cyan';
};

const LLMUsageTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState('30'); // days
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Get auth token from localStorage
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      // Build query params
      const today = new Date();
      let startDate, endDate;

      if (customStart && customEnd) {
        startDate = customStart;
        endDate = customEnd;
      } else {
        endDate = today.toISOString().split('T')[0];
        const start = new Date(today);
        start.setDate(start.getDate() - parseInt(dateRange));
        startDate = start.toISOString().split('T')[0];
      }

      const params = new URLSearchParams({
        start: startDate,
        end: endDate,
      });

      const response = await fetch(`${API_BASE_URL}/api/llm-usage-stats/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Please log in to view LLM usage statistics');
        } else if (response.status === 403) {
          setError('Admin access required to view LLM usage statistics');
        } else {
          setError(`Failed to fetch data: ${response.status}`);
        }
        setLoading(false);
        return;
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('LLM Usage fetch error:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, [dateRange, customStart, customEnd]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData();
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.600">Loading LLM usage data...</Text>
        </VStack>
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Card bg="red.50" borderColor="red.200" borderWidth={1}>
          <CardBody>
            <Text color="red.600" fontWeight="medium">{error}</Text>
            <Text color="gray.600" mt={2}>
              {error.includes('Admin') 
                ? 'LLM usage statistics are only available to administrators.'
                : 'Please check your connection and try again.'}
            </Text>
            <HStack mt={4}>
              <Input
                type="date"
                placeholder="Start date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                maxW="200px"
              />
              <Text>to</Text>
              <Input
                type="date"
                placeholder="End date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                maxW="200px"
              />
              <Select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                maxW="150px"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
              </Select>
              <Text color="gray.500" fontSize="sm" onClick={handleRefresh} cursor="pointer">
                Clear custom dates to use presets
              </Text>
            </HStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  if (!data || !data.summary) {
    return (
      <Box p={6}>
        <Card>
          <CardBody>
            <Text>No data available</Text>
          </CardBody>
        </Card>
      </Box>
    );
  }

  const { summary, model_breakdown, top_users, timeline, date_range } = data;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="md">LLM Usage Statistics</Heading>
          <Text fontSize="sm" color="gray.600">
            {date_range?.start && date_range?.end 
              ? `${formatDate(date_range.start)} to ${formatDate(date_range.end)}`
              : 'Select a date range'}
          </Text>
        </VStack>
        <HStack>
          <Input
            type="date"
            placeholder="Start"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            maxW="180px"
            size="sm"
          />
          <Text color="gray.500">to</Text>
          <Input
            type="date"
            placeholder="End"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            maxW="180px"
            size="sm"
          />
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            maxW="150px"
            size="sm"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </Select>
          <Badge colorScheme="blue" cursor="pointer" onClick={handleRefresh}>
            Refresh
          </Badge>
        </HStack>
      </HStack>

      {/* Summary Stats */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Total Queries</StatLabel>
              <StatNumber>{formatNumber(summary.total_queries)}</StatNumber>
              <StatHelpText>LLM API calls</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Total Tokens</StatLabel>
              <StatNumber>{formatNumber(summary.total_tokens)}</StatNumber>
              <StatHelpText>
                Avg: {formatNumber(summary.avg_total_tokens)} per query
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Input Tokens</StatLabel>
              <StatNumber>{formatNumber(summary.total_input_tokens)}</StatNumber>
              <StatHelpText>
                Avg: {formatNumber(summary.avg_input_tokens)} per query
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Output Tokens</StatLabel>
              <StatNumber>{formatNumber(summary.total_output_tokens)}</StatNumber>
              <StatHelpText>
                Avg: {formatNumber(summary.avg_output_tokens)} per query
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Estimated Cost</StatLabel>
              <StatNumber color="green.600">
                ${typeof summary.total_cost_usd === 'number' ? summary.total_cost_usd.toFixed(4) : '0.0000'}
              </StatNumber>
              <StatHelpText>Based on API pricing</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Unique Users</StatLabel>
              <StatNumber>{formatNumber(summary.unique_users)}</StatNumber>
              <StatHelpText>Using LLM features</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Timeline Table - Last 7 days */}
      {timeline && timeline.length > 0 && (
        <Card mb={6}>
          <CardHeader>
            <Heading size="sm">Token Usage Over Time (Last 7 Days)</Heading>
          </CardHeader>
          <CardBody>
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th isNumeric>Queries</Th>
                    <Th isNumeric>Total Tokens</Th>
                    <Th isNumeric>Unique Users</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {timeline.slice(-7).map((t, idx) => (
                    <Tr key={idx}>
                      <Td fontWeight="medium">{formatDate(t.period_start)}</Td>
                      <Td isNumeric>{formatNumber(t.total_queries)}</Td>
                      <Td isNumeric>{formatNumber(t.total_tokens)}</Td>
                      <Td isNumeric>{formatNumber(t.unique_users)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
      )}

      {/* LLM Model Breakdown Statistics */}
      <Card mb={6}>
        <CardHeader>
          <Heading size="sm">LLM Model Consumption Breakdown</Heading>
        </CardHeader>
        <CardBody>
          {model_breakdown && model_breakdown.length > 0 ? (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Model Name</Th>
                    <Th isNumeric>Queries</Th>
                    <Th isNumeric>Input Tokens</Th>
                    <Th isNumeric>Output Tokens</Th>
                    <Th isNumeric>Total Tokens</Th>
                    <Th isNumeric>Est. Cost (USD)</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {model_breakdown.map((m, idx) => (
                    <Tr key={`${m.model_used}-${idx}`}>
                      <Td fontWeight="medium">
                        <Badge colorScheme={getModelBadgeColor(m.model_used)} p={1} borderRadius="md" variant="subtle">
                          {m.model_used}
                        </Badge>
                      </Td>
                      <Td isNumeric>{formatNumber(m.total_queries)}</Td>
                      <Td isNumeric>{formatNumber(m.total_input_tokens)}</Td>
                      <Td isNumeric>{formatNumber(m.total_output_tokens)}</Td>
                      <Td isNumeric fontWeight="semibold">{formatNumber(m.total_tokens)}</Td>
                      <Td isNumeric color="green.600" fontWeight="medium">
                        ${typeof m.total_cost_usd === 'number' ? m.total_cost_usd.toFixed(4) : '0.0000'}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <Box textAlign="center" py={4}>
              <Text color="gray.500">No model usage statistics available.</Text>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Top Users Table */}
      <Card mb={6}>
        <CardHeader>
          <Flex justify="space-between" align="center" wrap="wrap">
            <Heading size="sm">Top Users by Token Consumption</Heading>
            <Badge colorScheme="purple" fontSize="xs" p={2} borderRadius="md">
              User Token Limit: {formatNumber(LLM_USER_TOKEN_LIMIT)} tokens
            </Badge>
          </Flex>
        </CardHeader>
        <CardBody>
          {top_users && top_users.length > 0 ? (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Rank</Th>
                    <Th>User</Th>
                    <Th>Models Used</Th>
                    <Th isNumeric>Queries</Th>
                    <Th isNumeric>Total Tokens</Th>
                    <Th isNumeric>% of Total</Th>
                    <Th isNumeric>Limit Progress</Th>
                    <Th isNumeric>Avg/Query</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {top_users.map((user, idx) => {
                    const limitPercent = Math.min(100, (user.total_tokens / LLM_USER_TOKEN_LIMIT) * 100);
                    const hasExceeded = user.total_tokens > LLM_USER_TOKEN_LIMIT;
                    const progressColorScheme = limitPercent > 90 ? 'red' : limitPercent > 70 ? 'orange' : 'green';
                    
                    return (
                      <Tr key={`${user.user_id || 'unknown'}-${idx}`}>
                        <Td>
                          <Badge
                            colorScheme={idx === 0 ? 'yellow' : idx === 1 ? 'gray' : idx === 2 ? 'orange' : 'blue'}
                          >
                            #{user.rank || idx + 1}
                          </Badge>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium">
                              {user.name || user.username || 'Unknown User'}
                            </Text>
                            {user.email && (
                              <Text fontSize="xs" color="gray.400">
                                {user.email}
                              </Text>
                            )}
                            <Text fontSize="xs" color="gray.500">
                              {user.schema || 'Unknown Schema'}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <HStack spacing={1} wrap="wrap">
                            {user.models_used && user.models_used.length > 0 ? (
                              user.models_used.map((mu, uidx) => (
                                <Tooltip 
                                  key={uidx} 
                                  label={`${formatNumber(mu.total_tokens)} tokens consumed`}
                                  hasArrow
                                >
                                  <Badge colorScheme={getModelBadgeColor(mu.model)} size="xs" variant="outline" fontSize="2xs">
                                    {mu.model}
                                  </Badge>
                                </Tooltip>
                              ))
                            ) : (
                              <Text fontSize="xs" color="gray.400">none</Text>
                            )}
                          </HStack>
                        </Td>
                        <Td isNumeric>{formatNumber(user.total_queries)}</Td>
                        <Td isNumeric fontWeight="semibold">
                          {formatNumber(user.total_tokens)}
                        </Td>
                        <Td isNumeric>
                          <HStack justify="flex-end" spacing={2}>
                            <Progress
                              value={user.percent_of_total || 0}
                              size="sm"
                              colorScheme="blue"
                              maxW="60px"
                              w="60px"
                              borderRadius="full"
                            />
                            <Text fontSize="sm" minW="45px" textAlign="right">
                              {formatPercent(user.percent_of_total)}
                            </Text>
                          </HStack>
                        </Td>
                        <Td isNumeric>
                          <HStack justify="flex-end" spacing={2}>
                            <Tooltip label={`${formatPercent((user.total_tokens / LLM_USER_TOKEN_LIMIT) * 100)} of limit`}>
                              <Progress
                                value={limitPercent}
                                size="sm"
                                colorScheme={progressColorScheme}
                                maxW="60px"
                                w="60px"
                                borderRadius="full"
                              />
                            </Tooltip>
                            <Badge colorScheme={hasExceeded ? 'red' : 'gray'} variant="subtle" fontSize="2xs">
                              {hasExceeded ? 'Exceeded' : `${Math.round((user.total_tokens / LLM_USER_TOKEN_LIMIT) * 100)}%`}
                            </Badge>
                          </HStack>
                        </Td>
                        <Td isNumeric>
                          {formatNumber(
                            user.total_queries > 0
                               ? Math.round(user.total_tokens / user.total_queries)
                               : 0
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <Box textAlign="center" py={8}>
              <Text color="gray.500">
                No LLM usage data available for this period.
              </Text>
              <Text color="gray.400" fontSize="sm">
                Token tracking requires database migration and AI feature usage.
              </Text>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Models List Badge Summary */}
      {summary.models_used && summary.models_used.length > 0 && (
        <Card>
          <CardHeader>
            <Heading size="sm">Unique Models Referenced</Heading>
          </CardHeader>
          <CardBody>
            <HStack wrap="wrap" spacing={2}>
              {summary.models_used.map((model, idx) => (
                <Badge key={idx} colorScheme={getModelBadgeColor(model)} fontSize="sm" p={2} borderRadius="md">
                  {model}
                </Badge>
              ))}
            </HStack>
          </CardBody>
        </Card>
      )}
    </Box>
  );
};

export default LLMUsageTab;